import { etopoSoundingTool } from "../etopo-sounding/index.js";

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

const LAYERS = [
  {
    id: "ehydro-survey-job",
    name: "USACE eHydro Survey Job",
    type: "esri-feature",
    url: "https://spatial.usace.army.mil/opjarcgis/rest/services/ehydro/SurveyJob/FeatureServer/0",
    opacity: 0.9,
    options: {
      simplifyFactor: 0.35,
      precision: 5,
      interactive: true,
      outFields: ["*"],
      attribution: "USACE eHydro",
    },
  },
  {
    id: "ehydro-channel-reach",
    name: "USACE eHydro Channel Reach",
    type: "esri-feature",
    url: "https://spatial.usace.army.mil/opjarcgis/rest/services/ehydro/ChannelReach/FeatureServer/0",
    opacity: 0.9,
    options: {
      simplifyFactor: 0.35,
      precision: 5,
      interactive: true,
      outFields: ["*"],
      attribution: "USACE eHydro",
    },
  },
];

function findAttr(attrs, keys) {
  for (const key of keys) {
    if (attrs[key] != null && String(attrs[key]).trim() !== "") return attrs[key];
    const alt = Object.keys(attrs).find((k) => String(k).toLowerCase() === String(key).toLowerCase());
    if (alt && attrs[alt] != null && String(attrs[alt]).trim() !== "") return attrs[alt];
  }
  return "";
}

function popupHtml(feature) {
  const attrs = feature?.properties || feature?.attributes || {};
  const name = findAttr(attrs, ["SURVEYNAME", "SURVEY_NAME", "NAME", "JOB_NAME", "REACHNAME", "REACH_NAME"]) || "eHydro Feature";
  const district = findAttr(attrs, ["DISTRICT", "DIST_NAME", "DISTRICT_NAME"]);
  const project = findAttr(attrs, ["PROJECT", "PROJECT_NAME", "PROJ_NAME"]);
  const fromDate = findAttr(attrs, ["SURVEYDATE", "SURVEY_DATE", "START_DATE", "DATE"]);
  const toDate = findAttr(attrs, ["END_DATE", "SURVEY_END", "ENDDATE"]);
  const surveyId = findAttr(attrs, ["SURVEYID", "SURVEY_ID", "OBJECTID", "GLOBALID"]);
  const rows = [
    ["Name", name],
    ["Survey ID", surveyId],
    ["District", district],
    ["Project", project],
    ["Date", [fromDate, toDate].filter(Boolean).join(" to ") || fromDate],
  ].filter((r) => String(r[1] || "").trim());
  const extraKeys = Object.keys(attrs).slice(0, 20);
  const extras = extraKeys.map((k) => `<tr><td style="opacity:.75; padding:2px 6px 2px 0;">${esc(k)}</td><td style="padding:2px 0;">${esc(attrs[k])}</td></tr>`).join("");
  return `
    <div style="min-width:260px;">
      <div style="font-weight:700; margin-bottom:6px;">${esc(name)}</div>
      <table style="width:100%; border-collapse:collapse; font-size:12px;">
        ${rows.map((r) => `<tr><td style="opacity:.75; padding:2px 6px 2px 0; white-space:nowrap;">${esc(r[0])}</td><td style="padding:2px 0;">${esc(r[1])}</td></tr>`).join("")}
      </table>
      <details style="margin-top:6px;">
        <summary style="cursor:pointer;">Raw attributes</summary>
        <table style="width:100%; border-collapse:collapse; font-size:11px; margin-top:4px;">${extras}</table>
      </details>
    </div>
  `;
}

function styleByRecency(feature) {
  const attrs = feature?.properties || feature?.attributes || {};
  const fromDate = findAttr(attrs, ["SURVEYDATE", "SURVEY_DATE", "START_DATE", "DATE"]);
  const ms = Date.parse(String(fromDate || ""));
  const ageYears = Number.isFinite(ms) ? ((Date.now() - ms) / (1000 * 60 * 60 * 24 * 365.25)) : null;
  if (ageYears == null) return { color: "#94a3b8", fillColor: "#64748b", fillOpacity: 0.16, weight: 1.2 };
  if (ageYears <= 2) return { color: "#22c55e", fillColor: "#16a34a", fillOpacity: 0.22, weight: 1.4 };
  if (ageYears <= 5) return { color: "#eab308", fillColor: "#ca8a04", fillOpacity: 0.2, weight: 1.3 };
  if (ageYears <= 10) return { color: "#f97316", fillColor: "#ea580c", fillOpacity: 0.18, weight: 1.2 };
  return { color: "#ef4444", fillColor: "#b91c1c", fillOpacity: 0.14, weight: 1.1 };
}

export const ehydroTool = {
  id: "ehydro",
  name: "USACE eHydro",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let status = "Ready.";
  let stackPickEnabled = true;
  let mapClickBound = false;
  let mapDomClickBound = false;
  let mapDomHost = null;
  let docClickBound = false;
  let importBusy = false;
  let importProgress = { active: false, ratio: 0, message: "" };

  function setImportProgress(active, ratio = 0, message = "") {
    importProgress = {
      active: !!active,
      ratio: Math.max(0, Math.min(1, Number(ratio) || 0)),
      message: String(message || ""),
    };
    render();
  }

  function inferSourceCRS(attrs = {}) {
    const isLikelyHorizontalEpsg = (n) => {
      const code = Number(n);
      if (!Number.isFinite(code)) return false;
      if (code === 4326 || code === 3857 || code === 3395) return true;
      if (code >= 2000 && code <= 3999) return true; // many projected CRS
      if (code >= 10000 && code <= 39999) return true; // ESRI-style projected ranges occasionally exposed
      return false;
    };
    const entries = Object.entries(attrs || {});
    const candidates = [];
    const values = [];
    for (const [k, v] of entries) {
      const key = String(k || "").toLowerCase();
      const val = String(v ?? "").trim();
      if (!val) continue;
      values.push(`${key}:${val}`);
      if (!/(epsg|wkid|srid|crs|proj|projection|datum|zone)/i.test(key)) continue;
      candidates.push(`${key}:${val}`);
    }
    const raw = candidates.join(" | ");
    const all = values.join(" | ");
    if (!all) return { code: null, basis: "none" };
    const epsgMatch = raw.match(/epsg[:\s_-]*(\d{3,6})/i);
    if (epsgMatch && isLikelyHorizontalEpsg(epsgMatch[1])) return { code: `EPSG:${epsgMatch[1]}`, basis: "epsg" };
    const wkidMatch = raw.match(/wkid[:\s_-]*(\d{3,6})/i);
    if (wkidMatch && isLikelyHorizontalEpsg(wkidMatch[1])) return { code: `EPSG:${wkidMatch[1]}`, basis: "wkid" };
    const sridMatch = raw.match(/srid[:\s_-]*(\d{3,6})/i);
    if (sridMatch && isLikelyHorizontalEpsg(sridMatch[1])) return { code: `EPSG:${sridMatch[1]}`, basis: "srid" };
    const bareCode = raw.match(/\b(2\d{3,5}|3\d{3,5}|4\d{3,5})\b/);
    if (bareCode && isLikelyHorizontalEpsg(bareCode[1])) return { code: `EPSG:${bareCode[1]}`, basis: "numeric" };

    // Metadata-derived UTM fallback: datum + zone -> EPSG
    const zoneMatch = all.match(/\butm[^0-9]{0,8}zone[^0-9]{0,8}(\d{1,2})\b/i) || all.match(/\bzone[^0-9]{0,8}(\d{1,2})\b/i);
    const zone = zoneMatch ? Number(zoneMatch[1]) : null;
    const south = /\b(south|southern)\b/i.test(all);
    const isNad83 = /\bnad\s*83\b|\bnad83\b/i.test(all);
    const isWgs84 = /\bwgs\s*84\b|\bwgs84\b/i.test(all);
    if (zone && zone >= 1 && zone <= 60) {
      if (isNad83 && !south) return { code: `EPSG:${26900 + zone}`, basis: "utm-nad83-zone" };
      if (isWgs84) return { code: `EPSG:${(south ? 32700 : 32600) + zone}`, basis: "utm-wgs84-zone" };
    }
    return { code: null, basis: "unknown" };
  }

  function fileNameFromUrl(url) {
    try {
      const u = new URL(String(url || ""));
      const part = (u.pathname || "").split("/").filter(Boolean).pop() || "";
      return decodeURIComponent(part) || "ehydro.xyz";
    } catch {
      return "ehydro.xyz";
    }
  }

  async function importDataDownloadAsSoundings(sourceUrl, layerName, sourceCRS, hintLat, hintLon) {
    const url = String(sourceUrl || "").trim();
    if (!url) throw new Error("No data URL found.");
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Data download failed (HTTP ${res.status}).`);
    const totalBytes = Number(res.headers.get("content-length") || 0);
    const contentType = String(res.headers.get("content-type") || "").toLowerCase();
    const looksZip = /\.zip($|\?)/i.test(url) || contentType.includes("application/zip") || contentType.includes("application/x-zip") || contentType.includes("application/octet-stream");
    const reader = res.body?.getReader?.();
    let text = "";
    let inferredFileName = fileNameFromUrl(url);
    if (looksZip) {
      const bytes = [];
      let loaded = 0;
      if (reader) {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          bytes.push(value);
          loaded += value?.byteLength || 0;
          if (totalBytes > 0) {
            setImportProgress(true, Math.max(0.01, Math.min(0.7, (loaded / totalBytes) * 0.7)), `Downloading ZIP... ${Math.round((loaded / totalBytes) * 100)}%`);
          } else {
            setImportProgress(true, 0.35, `Downloading ZIP... ${(loaded / (1024 * 1024)).toFixed(1)} MB`);
          }
        }
      } else {
        const ab = await res.arrayBuffer();
        bytes.push(new Uint8Array(ab));
      }
      const total = bytes.reduce((n, b) => n + (b?.byteLength || 0), 0);
      const merged = new Uint8Array(total);
      let off = 0;
      for (const b of bytes) {
        merged.set(b, off);
        off += b.byteLength || 0;
      }
      setImportProgress(true, 0.72, "Extracting ZIP...");
      const { default: JSZip } = await import("jszip");
      const zip = await JSZip.loadAsync(merged.buffer);
      const files = Object.values(zip.files || {}).filter((f) => !f.dir);
      const preferred = files.find((f) => /\.(xyz|csv|txt|pts|dat|asc)$/i.test(f.name))
        || files.find((f) => /\.(json|geojson)$/i.test(f.name))
        || files[0];
      if (!preferred) throw new Error("ZIP contains no files.");
      inferredFileName = String(preferred.name || inferredFileName);
      text = await preferred.async("text");
      setImportProgress(true, 0.75, `Extracted ${inferredFileName}`);
    } else if (reader) {
      const decoder = new TextDecoder();
      let loaded = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        loaded += value?.byteLength || 0;
        text += decoder.decode(value, { stream: true });
        if (totalBytes > 0) {
          setImportProgress(true, Math.max(0.01, Math.min(0.75, (loaded / totalBytes) * 0.75)), `Downloading data... ${Math.round((loaded / totalBytes) * 100)}%`);
        } else {
          setImportProgress(true, 0.35, `Downloading data... ${(loaded / (1024 * 1024)).toFixed(1)} MB`);
        }
      }
      text += decoder.decode();
    } else {
      text = await res.text();
      setImportProgress(true, 0.75, "Download complete.");
    }
    if (!text.trim()) throw new Error("Downloaded data is empty.");
    setImportProgress(true, 0.76, "Handing off to Soundings importer...");
    try {
      etopoSoundingTool?.init?.(app);
    } catch {}
    return await new Promise((resolve, reject) => {
      let settled = false;
      let acked = false;
      const watchdog = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error("Soundings importer did not respond. Open the Soundings tool once and retry."));
      }, 8000);
      const done = (fn) => (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(watchdog);
        fn(value);
      };
      app.events.emit("soundings:import:text", {
        text,
        sourceUrl: url,
        sourceCRS: String(sourceCRS || "EPSG:4326"),
        fileName: inferredFileName,
        layerName: String(layerName || "eHydro Survey").trim() || "eHydro Survey",
        zoom: true,
        hintLat: Number(hintLat),
        hintLon: Number(hintLon),
        progress: (p = {}) => {
          if (!acked) {
            acked = true;
            setImportProgress(true, 0.78, "Soundings importer connected...");
          }
          const stage = String(p?.stage || "");
          const base = stage === "parse" ? 0.75 : stage === "build" ? 0.95 : 0.98;
          const span = stage === "parse" ? 0.2 : stage === "build" ? 0.03 : 0.02;
          const localRatio = Math.max(0, Math.min(1, Number(p?.ratio) || 0));
          setImportProgress(true, base + (localRatio * span), String(p?.message || "Processing..."));
        },
        resolve: done(resolve),
        reject: done(reject),
      });
    });
  }

  const layerService = () => app?.services?.layers || null;
  const existingByMetaId = (metaId) => (layerService()?.listLayers?.() || []).find((layer) => String(layer?.meta?.ehydroId || "") === String(metaId));

  function addLayer(def) {
    const svc = layerService();
    if (!svc) {
      status = "Layer service unavailable.";
      render();
      return;
    }
    const existing = existingByMetaId(def.id);
    if (existing?.id) {
      status = `${def.name} already loaded.`;
      render();
      return;
    }
    svc.addLayer("ehydro", {
      id: def.id,
      kind: "overlay",
      type: def.type,
      name: def.name,
      url: def.url,
      visible: true,
      opacity: def.opacity,
      options: {
        ...(def.options || {}),
        popupBuilder: popupHtml,
        styleBuilder: styleByRecency,
      },
      meta: { sourceType: "ehydro", ehydroId: def.id },
    });
    status = `Loaded ${def.name}.`;
    render();
  }

  function removeLayer(def) {
    const svc = layerService();
    const existing = existingByMetaId(def.id);
    if (!svc || !existing?.id) {
      status = `${def.name} not loaded.`;
      render();
      return;
    }
    svc.removeLayer(existing.id);
    status = `Removed ${def.name}.`;
    render();
  }

  function isLoaded(def) {
    return !!existingByMetaId(def.id);
  }

  function loadedDefs() {
    return LAYERS.filter((def) => isLoaded(def));
  }

  async function queryAtLatLng(def, lat, lon) {
    const q = new URL(`${def.url.replace(/\/+$/, "")}/query`);
    q.searchParams.set("f", "json");
    q.searchParams.set("where", "1=1");
    q.searchParams.set("geometryType", "esriGeometryPoint");
    q.searchParams.set("spatialRel", "esriSpatialRelIntersects");
    q.searchParams.set("inSR", "4326");
    q.searchParams.set("outSR", "4326");
    q.searchParams.set("returnGeometry", "false");
    q.searchParams.set("outFields", "*");
    q.searchParams.set("geometry", JSON.stringify({ x: lon, y: lat, spatialReference: { wkid: 4326 } }));
    const res = await fetch(q.toString());
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return Array.isArray(json?.features) ? json.features : [];
  }

function itemTitle(attrs) {
    return String(
      findAttr(attrs, ["SURVEYNAME", "SURVEY_NAME", "NAME", "JOB_NAME", "REACHNAME", "REACH_NAME"]) ||
      findAttr(attrs, ["PROJECT", "PROJECT_NAME", "PROJ_NAME"]) ||
      findAttr(attrs, ["SURVEYID", "SURVEY_ID", "OBJECTID", "GLOBALID"]) ||
      "eHydro Feature"
  );
}

function parseSurveyTimeMs(attrs) {
  const parseOne = (raw) => {
    if (raw == null || raw === "") return null;
    const s = String(raw).trim();
    if (!s) return null;
    const n = Number(s);
    if (Number.isFinite(n)) {
      if (n > 1e12) return n; // epoch ms
      if (n > 1e9) return n * 1000; // epoch sec
      if (n >= 19000101 && n <= 21001231 && /^\d{8}$/.test(s)) {
        const y = Number(s.slice(0, 4));
        const m = Number(s.slice(4, 6));
        const d = Number(s.slice(6, 8));
        const ms = Date.UTC(y, Math.max(0, m - 1), d);
        return Number.isFinite(ms) ? ms : null;
      }
      if (n >= 30000 && n <= 90000) {
        // Excel serial date (days since 1899-12-30)
        const ms = Date.UTC(1899, 11, 30) + (n * 86400000);
        return Number.isFinite(ms) ? ms : null;
      }
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(s) || /^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(s)) {
      const ms = Date.parse(s);
      if (Number.isFinite(ms)) return ms;
    }
    const ms = Date.parse(s);
    return Number.isFinite(ms) ? ms : null;
  };

  const preferred = [
    "SURVEYDATE", "SURVEY_DATE", "START_DATE", "END_DATE", "DATE",
    "SURVEY_START", "SURVEYSTARTDATE", "SURVEY_END", "SURVEYENDDATE",
  ];
  for (const key of preferred) {
    const v = findAttr(attrs, [key]);
    const ms = parseOne(v);
    if (Number.isFinite(ms)) return ms;
  }

  // Try year/month/day split fields.
  const y = Number(findAttr(attrs, ["YEAR", "SURVEY_YEAR", "SURVEYYEAR"]));
  const m = Number(findAttr(attrs, ["MONTH", "SURVEY_MONTH", "SURVEYMONTH"]));
  const d = Number(findAttr(attrs, ["DAY", "SURVEY_DAY", "SURVEYDAY"]));
  if (Number.isFinite(y) && y >= 1900 && y <= 2100) {
    const ms = Date.UTC(y, Number.isFinite(m) ? Math.max(0, m - 1) : 0, Number.isFinite(d) ? d : 1);
    if (Number.isFinite(ms)) return ms;
  }

  // Last resort: scan any key that looks date/time related.
  for (const [k, v] of Object.entries(attrs || {})) {
    if (!/(date|time|survey)/i.test(String(k))) continue;
    const ms = parseOne(v);
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

function fmtDate(ms) {
  if (!Number.isFinite(ms)) return "Date unknown";
  try {
    return new Date(ms).toISOString().slice(0, 10);
  } catch {
    return "Date unknown";
  }
}

function pickLinkEntries(attrs) {
  const rows = [];
  for (const [k, v] of Object.entries(attrs || {})) {
    const key = String(k || "");
    const val = String(v ?? "").trim();
    if (!val) continue;
    const lower = key.toLowerCase();
    const urlLike = /^https?:\/\//i.test(val);
    const keyHints = ["url", "link", "report", "plot", "data", "download", "doc", "pdf", "chart"];
    const hinted = keyHints.some((h) => lower.includes(h));
    if (!urlLike && !hinted) continue;
    const match = val.match(/https?:\/\/[^\s<>"']+/i);
    const href = match ? match[0] : (urlLike ? val : "");
    if (!href) continue;
    const kLower = key.toLowerCase();
    let label = key;
    if (kLower.includes("source") && kLower.includes("location")) label = "Data Download";
    if (kLower.includes("plotsheet") && kLower.includes("location")) label = "PDF Plot Download";
    rows.push({ key, href, label });
  }
  const unique = [];
  const seen = new Set();
  for (const row of rows) {
    const sig = `${row.label}|${row.href}`.toLowerCase();
    if (seen.has(sig)) continue;
    seen.add(sig);
    unique.push(row);
  }
  return unique.slice(0, 8);
}

  async function onMapClick(e) {
    if (!stackPickEnabled) return;
    const defs = loadedDefs();
    if (!defs.length) return;
    const lat = Number(e?.latlng?.lat);
    const lon = Number(e?.latlng?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    try {
      const results = await Promise.all(defs.map(async (def) => {
        try {
          const features = await queryAtLatLng(def, lat, lon);
          return features.map((f) => ({ def, attrs: f?.attributes || f?.properties || {} }));
        } catch {
          return [];
        }
      }));
      const hits = results.flat();
      if (!hits.length) return;
      const rows = hits
        .map((hit) => ({ ...hit, timeMs: parseSurveyTimeMs(hit.attrs || {}) }))
        .sort((a, b) => (Number(b.timeMs || -Infinity) - Number(a.timeMs || -Infinity)))
        .slice(0, 80)
        .map((hit) => {
        const attrs = hit.attrs || {};
        const title = itemTitle(attrs);
        const surveyId = String(
          findAttr(attrs, ["SURVEYID", "SURVEY_ID", "OBJECTID", "GLOBALID"]) ||
          title
        ).trim();
        const dateText = fmtDate(hit.timeMs);
        const links = pickLinkEntries(attrs);
        return `
          <details style="margin-top:4px;">
            <summary style="cursor:pointer;"><span style="font-weight:700;">${esc(dateText)}</span> - ${esc(surveyId)}</summary>
            <div class="small" style="margin-top:4px;">
              ${links.length
                ? links.map((row) => {
                  const label = String(row.label || row.key || "");
                  if (label === "Data Download") {
                    const inferred = inferSourceCRS(attrs);
                    const crs = inferred?.code || "EPSG:4326";
                    const basis = inferred?.basis || "fallback";
                    return `<div><button class="btn small" data-ehydro-import-url="${esc(row.href)}" data-ehydro-import-name="${esc(surveyId)}" data-ehydro-import-crs="${esc(crs)}" data-ehydro-import-crs-basis="${esc(basis)}" data-ehydro-import-lat="${esc(lat)}" data-ehydro-import-lon="${esc(lon)}">Data Download</button></div>`;
                  }
                  return `<div><a href="${esc(row.href)}" target="_blank" rel="noopener noreferrer">${esc(label)}</a></div>`;
                }).join("")
                : "No links found in this record."}
            </div>
          </details>
        `;
      }).join("");
      const html = `
        <div style="min-width:320px; max-height:320px; overflow:auto;">
          <div style="font-weight:700; margin-bottom:4px;">eHydro surveys at click: ${hits.length} (newest first)</div>
          ${rows}
        </div>
      `;
      const map = app?.map?.instance;
      if (map && window.L?.popup) {
        window.L.popup({ maxWidth: 420 }).setLatLng(e.latlng).setContent(html).openOn(map);
      }
    } catch {}
  }

  async function onMapDomClick(ev) {
    if (!stackPickEnabled) return;
    const defs = loadedDefs();
    if (!defs.length) return;
    const target = ev?.target;
    if (target?.closest?.(".leaflet-popup")) return;
    if (target?.closest?.(".leaflet-control")) return;
    const isVectorHit = !!target?.closest?.(".leaflet-interactive");
    if (!isVectorHit) return;
    const map = app?.map?.instance;
    if (!map || typeof map.mouseEventToLatLng !== "function") return;
    const latlng = map.mouseEventToLatLng(ev);
    if (!latlng) return;
    // Prefer stacked picker over single top-feature popup.
    ev.preventDefault?.();
    ev.stopPropagation?.();
    try {
      if (window.L?.DomEvent?.stop) window.L.DomEvent.stop(ev);
    } catch {}
    await onMapClick({ latlng });
  }

  async function onDocumentClick(ev) {
    const btn = ev?.target?.closest?.("[data-ehydro-import-url]");
    if (!btn) return;
    if (importBusy) return;
    ev.preventDefault?.();
    ev.stopPropagation?.();
    importBusy = true;
    const href = btn.getAttribute("data-ehydro-import-url") || "";
    const layerName = btn.getAttribute("data-ehydro-import-name") || "eHydro Survey";
    const sourceCRS = btn.getAttribute("data-ehydro-import-crs") || "EPSG:4326";
    const crsBasis = btn.getAttribute("data-ehydro-import-crs-basis") || "fallback";
    const hintLat = Number(btn.getAttribute("data-ehydro-import-lat"));
    const hintLon = Number(btn.getAttribute("data-ehydro-import-lon"));
    status = `Importing eHydro data as soundings (CRS ${sourceCRS} via ${crsBasis})...`;
    setImportProgress(true, 0.01, "Starting import...");
    try {
      const out = await importDataDownloadAsSoundings(href, layerName, sourceCRS, hintLat, hintLon);
      status = `Imported ${Number(out?.pointCount || 0).toLocaleString()} soundings into "${String(out?.layerName || layerName)}".`;
    } catch (err) {
      status = `Import failed: ${err?.message || String(err)}`;
    } finally {
      importBusy = false;
      setImportProgress(false, 0, "");
    }
    render();
  }

  function ensureMapClickBinding() {
    const map = app?.map?.instance;
    if (!map || mapClickBound) return;
    map.on("click", onMapClick);
    mapClickBound = true;
    const host = map.getContainer?.();
    if (host && !mapDomClickBound) {
      host.addEventListener("click", onMapDomClick, true);
      mapDomHost = host;
      mapDomClickBound = true;
    }
    if (!docClickBound) {
      document.addEventListener("click", onDocumentClick, true);
      docClickBound = true;
    }
  }

  function removeMapClickBinding() {
    const map = app?.map?.instance;
    if (!map || !mapClickBound) return;
    map.off("click", onMapClick);
    mapClickBound = false;
    if (mapDomHost && mapDomClickBound) {
      mapDomHost.removeEventListener("click", onMapDomClick, true);
    }
    if (docClickBound) {
      document.removeEventListener("click", onDocumentClick, true);
    }
    mapDomHost = null;
    mapDomClickBound = false;
    docClickBound = false;
  }

  function render() {
    if (!containerEl) return;
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">USACE eHydro</div>
        <div class="small" style="margin-top:6px;">
          Quick-add USACE eHydro feature layers for survey coverage and channel framework context.
        </div>
        <div style="margin-top:10px; display:flex; flex-direction:column; gap:8px;">
          <label class="small" style="display:inline-flex; align-items:center; gap:8px;">
            <input type="checkbox" id="ehydro-stack-pick-enabled" ${stackPickEnabled ? "checked" : ""} />
            Click map to list all overlapping surveys
          </label>
          ${LAYERS.map((def) => {
            const loaded = isLoaded(def);
            return `
              <div class="row lm-add-row" style="justify-content:space-between; align-items:center;">
                <div class="small" style="min-width:0;">
                  <div style="font-weight:600;">${esc(def.name)}</div>
                  <div style="opacity:.8;">${esc(def.url)}</div>
                </div>
                <div style="display:flex; gap:6px;">
                  <button class="btn"${loaded ? " disabled" : ""} data-ehydro-add="${esc(def.id)}">Add</button>
                  <button class="btn"${loaded ? "" : " disabled"} data-ehydro-zoom="${esc(def.id)}">Zoom</button>
                  <button class="btn"${loaded ? "" : " disabled"} data-ehydro-remove="${esc(def.id)}">Remove</button>
                </div>
              </div>
            `;
          }).join("")}
        </div>
        <div style="margin-top:8px; ${importProgress.active ? "" : "display:none;"}">
          <div style="height:8px; border:1px solid #334155; border-radius:999px; overflow:hidden; background:#0b1220;">
            <div style="height:100%; width:${Math.round((Number(importProgress.ratio) || 0) * 100)}%; background:linear-gradient(90deg,#38bdf8,#22d3ee); transition:width 120ms linear;"></div>
          </div>
          <div class="small" style="margin-top:4px; color:#cbd5e1;">${esc(importProgress.message || "Working...")}</div>
        </div>
        <div class="small" style="margin-top:8px; color:#cbd5e1;">${esc(status)}</div>
      </div>
    `;
  }

  function onClick(event) {
    const addId = event.target?.closest?.("[data-ehydro-add]")?.getAttribute?.("data-ehydro-add");
    if (addId) {
      const def = LAYERS.find((it) => it.id === addId);
      if (def) addLayer(def);
      return;
    }
    const removeId = event.target?.closest?.("[data-ehydro-remove]")?.getAttribute?.("data-ehydro-remove");
    if (removeId) {
      const def = LAYERS.find((it) => it.id === removeId);
      if (def) removeLayer(def);
      return;
    }
    const zoomId = event.target?.closest?.("[data-ehydro-zoom]")?.getAttribute?.("data-ehydro-zoom");
    if (zoomId) {
      const svc = layerService();
      const def = LAYERS.find((it) => it.id === zoomId);
      if (!svc || !def) return;
      const existing = existingByMetaId(def.id);
      if (!existing?.id) {
        status = `${def.name} is not loaded.`;
        render();
        return;
      }
      try {
        svc.zoomTo(existing.id);
        status = `Zoomed to ${def.name}.`;
      } catch {
        status = `Could not zoom to ${def.name}.`;
      }
      render();
    }
    if (event.target?.id === "ehydro-stack-pick-enabled") {
      stackPickEnabled = !!event.target.checked;
      status = stackPickEnabled ? "Stacked picker enabled." : "Stacked picker disabled.";
      render();
    }
  }

  function mount(el) {
    if (containerEl) containerEl.removeEventListener("click", onClick);
    containerEl = el;
    render();
    ensureMapClickBinding();
    containerEl.addEventListener("click", onClick);
  }

  function destroy() {
    if (!containerEl) return;
    containerEl.removeEventListener("click", onClick);
    removeMapClickBinding();
    containerEl = null;
  }

  return {
    mount,
    destroy,
    snapshot: () => ({}),
    restore: () => {},
  };
}
